import React, { useEffect, useCallback } from 'react';

import {
  ModelType,
  SettingBookingTargetsLesson,
  Lesson,
} from '../../../../../interfaces/merchants/resources/books';

import {
  clearBookingSchoolTimeSlot,
  inputBookingTargets,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';

import InputBookingTargetLessonSelector from '../InputBookingTargetLessonSelector';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { useAppDispatch } from '../../../../../redux';

interface ElementProps {
  modelType: ModelType;
  settings: SettingBookingTargetsLesson;
  postable: boolean;
  renew: boolean;
  onSubmit: () => void;
  onRestart: () => void;
  withExpressFlow: boolean;
  resourcePublicId: string;
}

type Props = ElementProps;

const InputBookingTarget: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();

  const { settings, postable, resourcePublicId } = props;

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();

  useEffect(() => {
    const {
      settings: { selectedLesson },
    } = props;
    scrollToTop();

    if (!availableToUseSelectedLesson()) return;
    handleSubmit(selectedLesson!);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const availableToUseSelectedLesson = useCallback(() => {
    const {
      settings: { selectedLesson },
      renew,
    } = props;
    if (renew) return false;
    if (!selectedLesson) return false;
    else if (inputs.lesson) return false;
    return true;
  }, [props, inputs]);

  const handleSubmit = useCallback(
    async (lesson: Lesson) => {
      const { modelType, onSubmit, onRestart, withExpressFlow } = props;
      try {
        await dispatch(clearBookingSchoolTimeSlot());
        await dispatch(
          inputBookingTargets(
            modelType,
            inputs.merchantId!,
            resourcePublicId,
            { lesson },
            withExpressFlow,
            inputs.reservationId,
          ),
        );
        onSubmit();
      } catch (e) {
        if (e instanceof RestartReservationError) {
          onRestart();
        }
      }
    },
    [props, dispatch, inputs, resourcePublicId],
  );

  if (availableToUseSelectedLesson()) return null;
  return (
    <InputBookingTargetLessonSelector
      lessons={settings.lessons}
      postable={postable}
      onClick={handleSubmit}
    />
  );
};

export default InputBookingTarget;
